import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Switch, Route, Redirect, useLocation } from 'react-router-dom'
import { useGlobalState } from '@/state'
import { requestEzoicAds, destroyEzoicAds } from '@/utils/ezoicAds'

import Sidebar from '@/Layout/Sidebar'
import Header from '@/Layout/Header'
import ErrorHandler from '@/Layout/ErrorHander'
import ErrorNotification from '@/shared/ErrorNotification'
import ChangelogAnnouncementModal from '@/shared/ChangelogAnnouncementModal'
import SurveyPrompt from '@/shared/SurveyPrompt'
import { Toast } from '@/shared/Toast'
import Loading from '@/shared/Loading'

import TalentSearchContainer from '@/containers/TalentSearchContainer'
import SavedSearchProjectsContainer from '@/containers/SavedSearchProjectsContainer'
import ChangelogContainer from '@/containers/ChangelogContainer'
import CoinsContainer from '@/containers/CoinsContainer'
import CreditsContainer from '@/containers/CreditsContainer'
import DashboardContainer from '@/containers/DashboardContainer'
import FavoritesContainer from '@/containers/FavoritesContainer'
import FeedbackContainer from '@/containers/FeedbackContainer'
import AlertsContainer from '@/containers/AlertsContainer'
import ManageProjectsContainer from '@/containers/ManageProjectsContainer'
import MyRolesContainer from '@/containers/MyRolesContainer'
import AchievementsContainer from '@/containers/AchievementsContainer'
import PerksContainer from '@/containers/PerksContainer'
import PrivatePostsContainer from '@/containers/PrivatePostsContainer'
import ProfileContainer from '@/containers/ProfileContainer'
import RecommendationsContainer from '@/containers/RecommendationsContainer'
import EventsContainer from '@/containers/EventsContainer'
import SettingsContainer from '@/containers/SettingsContainer'
import SocialContainer from '@/containers/SocialContainer'
import SubmissionsContainer from '@/containers/SubmissionsContainer'
import SubscriptionContainer from '@/containers/SubscriptionContainer'
import UsersContainer from '@/containers/UsersContainer'
import ProjectSearchContainer from '@/containers/ProjectSearchContainer'
import SearchContainer from '@/containers/SearchContainer'
import AdvertisingCampaigns from '@/pages/Advertising/AdvertisingCampaigns'
import BillingHistory from '@/pages/Billing/BillingHistory'
import Receipt from '@/pages/Billing/Receipt'
import NewCampaignPage from '@/pages/Advertising/NewCampaignPage'

function usePageViews(currentUser) {
  const location = useLocation()

  useEffect(() => {
    if (typeof window.ga === 'function') {
      window.ga('set', 'page', location.pathname)
      window.ga('event', 'page_view')
    }
  }, [location])
}

// Route-level half of the Ezoic ad lifecycle for the SPA: teardown on
// navigation, plus a request for the new route.
//
// It is NOT the only trigger. Ad components also call requestEzoicAds() when
// they mount, because most pages render their slots only after an async fetch
// resolves — this effect fires while the page is still a spinner, so on its own
// it would request nothing and leave those slots dead (and Ezoic warning
// "No placeholder Ids are currently defined for Refresh()"). The request is
// debounced and idempotent, so the two triggers collapse into one call.
//
// Components never talk to ezstandalone directly; everything goes through
// utils/ezoicAds so there is one place that knows the call sequence. The legacy
// per-mount enable()/display()/define() chain stays gone — that was the thing
// that overwrote the placeholder set between routes.
//
// destroyAll() is skipped on the first route because nothing has displayed yet.
//
// Only runs inside the SPA; server-rendered pages request their own ads once
// per full page load and never reach this hook.
//
// Ad-free routes: the project start/edit forms get no ads at all. Those pages
// render no placeholder components, so the only ads that can appear there are
// the overlay units (anchor, interstitial, floating video) painted by this
// hook's own showAds() — skipping the request is what keeps them off. Teardown
// still runs so ads from the previous route don't linger over the form.
//
// An open DM thread is ad-free too: the composer is pinned to the bottom of the
// screen, exactly where the anchor unit paints, and a message box you can't
// see is worse than any ad is worth. The inbox keeps its ads.
const AD_FREE_ROUTES = /^\/manage\/projects\/[^/]+\/(start|edit)$|^\/private_messages\/\d+$/

// Routes that are an app view rather than a document: they fill the space
// under the header and scroll their own panes, instead of sitting in the
// centred container and scrolling <main>. Two nested scrollers is what made
// the messages page lurch every time a thread opened.
const FULL_BLEED_ROUTES = /^\/private_messages(\/|$)/

function useEzoicAds() {
  const location = useLocation()
  const isFirstRoute = useRef(true)

  useEffect(() => {
    const firstRoute = isFirstRoute.current
    isFirstRoute.current = false

    // Tear down the previous route's placeholders, then ask for the new
    // route's. The request is ALSO fired by each ad component on mount
    // (see utils/ezoicAds) because most pages render their slots only after an
    // async fetch — by the time data arrives this effect has long since run.
    if (!firstRoute) { destroyEzoicAds() }
    // The body class drives a display:none kill-switch in layouts/_ad_stability
    // that hides ANY Ezoic element on editing surfaces — including ones we
    // didn't render (dashboard-side automatic placements, stale overlays).
    // Editing pages must never jump. Skipping the request is not enough on its
    // own because injected elements can survive teardown.
    document.body.classList.toggle('ezoic-suppressed', AD_FREE_ROUTES.test(location.pathname))
    if (AD_FREE_ROUTES.test(location.pathname)) { return }
    requestEzoicAds()
  }, [location.pathname])
}

export default function App(props) {
  // eslint-disable-next-line no-unused-vars
  const [currentUser, setCurrentUser] = useGlobalState('currentUser')
  const [, setReleaseInfo] = useGlobalState('releaseInfo')
  const [, setEduAds] = useGlobalState('eduAds')
  const isComponentMounted = useRef(false)

  useEffect(() => {
    setCurrentUser(props.currentUser)
    setReleaseInfo(props.releaseInfo)
    setEduAds(props.eduAds)
    isComponentMounted.current = true
  }, [])

  usePageViews()
  useEzoicAds()
  const fullBleed = FULL_BLEED_ROUTES.test(useLocation().pathname)

  if (!isComponentMounted.current) {
    return <Loading full={true} />
  }

  return (
    // 100dvh on full-bleed routes: on phones 100vh runs under the browser's
    // toolbar, which is where a pinned composer lives. Browsers that don't know
    // dvh drop the inline value and keep h-screen.
    <div className="h-screen flex overflow-hidden dark:bg-gray-700 bg-gray-100" style={fullBleed ? { height: '100dvh' } : undefined}>
      <Sidebar />
      <div className="flex flex-col w-0 flex-1">
        <Header />
        <main className={fullBleed ? 'flex-1 min-h-0 flex flex-col' : 'overflow-y-auto'}>
          <div className={fullBleed ? 'flex-1 min-h-0 flex flex-col' : 'container mx-auto px-4 sm:px-6 lg:px-8 pb-16'}>
            <ErrorHandler>
              <Switch>
                <Route {...props} exact path={['/coins']}>
                  <CoinsContainer />
                </Route>
                <Route {...props} exact path={['/credits', '/credits/search', '/credits/new', '/credits/:id/edit', '/credits/:id/manage', '/credits/:slug']}>
                  <CreditsContainer />
                </Route>
                <Route {...props} path={['/events']}>
                  <EventsContainer />
                </Route>
                <Route {...props} path={['/dashboard', '/dashboard/profile_views']}>
                  <DashboardContainer />
                </Route>
                <Route {...props} path={['/saved_searches']}>
                  <SavedSearchProjectsContainer />
                </Route>
                <Route {...props} path={['/favorites']}>
                  <FavoritesContainer />
                </Route>
                <Route {...props} exact path={['/feedback', '/feedback/new', '/feedback/:slug']}>
                  <FeedbackContainer />
                </Route>
                <Route {...props} path={['/social']}>
                  <SocialContainer />
                </Route>
                <Route {...props} path={['/notifications']}>
                  <AlertsContainer />
                </Route>
                <Route {...props} path='/profile'>
                  <UsersContainer />
                </Route>
                <Route {...props} exact path={
                  [
                    '/manage/projects',
                    '/manage/projects/:id',
                    '/manage/projects/:id/edit',
                    '/manage/projects/:id/messages',
                    '/manage/projects/:id/analytics',
                    '/manage/projects/:id/submissions',
                    '/manage/projects/:id/start',
                    '/manage/projects/:id/roles',
                    '/manage/projects/:id/team'
                  ]
                }>
                  <ManageProjectsContainer />
                </Route>
                <Route {...props} path={['/myroles']}>
                  <MyRolesContainer />
                </Route>
                <Route {...props} path={['/achievements']}>
                  <AchievementsContainer />
                </Route>
                {/* /advertising itself is the server-rendered For Brands page. */}
                <Route {...props} exact path={['/advertising/campaigns']}>
                  <AdvertisingCampaigns />
                </Route>
                <Route {...props} exact path={['/advertising/new']}>
                  <NewCampaignPage />
                </Route>
                {/* Receipt route comes first — /settings/billing would otherwise
                    swallow it. */}
                <Route {...props} exact path={['/settings/billing/receipts/:receiptNumber']}>
                  <Receipt />
                </Route>
                <Route {...props} exact path={['/settings/billing']}>
                  <BillingHistory />
                </Route>
                <Route {...props} path={['/submissions', '/submissions/:id']}>
                  <SubmissionsContainer />
                </Route>
                <Route {...props} exact path={['/private_messages', '/private_messages/new', '/private_messages/:id']}>
                  <PrivatePostsContainer />
                </Route>
                <Route {...props} path={['/perks']}>
                  <PerksContainer />
                </Route>
                <Route {...props} path={['/recommendations']}>
                  <RecommendationsContainer />
                </Route>
                <Route {...props} path={['/settings']}>
                  <SettingsContainer />
                </Route>
                <Route {...props} path={['/changelog']}>
                  <ChangelogContainer />
                </Route>
                <Route {...props} path={['/subscriptions', '/membership', '/change_card']}>
                  <SubscriptionContainer />
                </Route>
                <Route {...props} path={['/talent', '/talent/:talent_type']}>
                  <TalentSearchContainer />
                </Route>
                <Route {...props} exact path='/search'>
                  <SearchContainer />
                </Route>
                <Route {...props} exact path={['/find_jobs', '/category/:category', '/']}>
                  <ProjectSearchContainer />
                </Route>
                <Route {...props} path='/m/:username'>
                  <ProfileContainer />
                </Route>
                {/* Legacy bare profile links (/:username) — client-side redirect to the
                    canonical /m/:username. Hard navs are 301'd server-side; this covers
                    in-app <Link> clicks that still point at the bare form. Keeps ?tab=… */}
                <Route
                  {...props}
                  exact
                  path='/:username'
                  render={({ match, location }) => (
                    <Redirect to={`/m/${match.params.username}${location.search}`} />
                  )}
                />
              </Switch>
            </ErrorHandler>
          </div>
        </main>
      </div>
      <ErrorNotification />
      <Toast />
      <SurveyPrompt />
      <ChangelogAnnouncementModal />
    </div>
  )
}

App.propTypes = {
  currentUser: PropTypes.object.isRequired,
  eduAds: PropTypes.array.isRequired,
  releaseInfo: PropTypes.string.isRequired
}
